import React from "react";
import YouTube from "react-youtube";
import { IoPlay } from "react-icons/io5";
import { event } from "react-ga";

import { playTimes } from "../../constants";

import * as Styled from "./index.styled";

interface Props {
  id: string;
  currentTry: number;
  startSeconds?: number;
}

const LOAD_ERROR_MESSAGE =
  "无法加载 YouTube 播放器，请检查网络是否能访问 YouTube 后再试。";
const PLAYBACK_ERROR_MESSAGE =
  "无法播放 YouTube 音频，请检查网络或浏览器自动播放设置后再试。";

export function Player({ id, currentTry, startSeconds = 0 }: Props) {
  const opts = {
    width: "200",
    height: "200",
    playerVars: {
      controls: 0 as const,
      disablekb: 1 as const,
      playsinline: 1 as const,
      rel: 0 as const,
      start: Math.max(0, Math.floor(startSeconds)),
    },
  };

  // react-youtube doesn't export types for this
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const playerRef = React.useRef<any>(null);
  const previousIdRef = React.useRef<string>(id);

  const currentPlayTime = playTimes[currentTry];
  const safeStartSeconds = Math.max(0, Math.floor(startSeconds));

  const [play, setPlay] = React.useState<boolean>(false);

  const [currentTime, setCurrentTime] = React.useState<number>(0);
  const currentElapsedTime = Math.max(currentTime - safeStartSeconds, 0);

  const [isReady, setIsReady] = React.useState<boolean>(false);
  const [isSlowToLoad, setIsSlowToLoad] = React.useState<boolean>(false);
  const [playerMessage, setPlayerMessage] = React.useState<string>("");

  React.useEffect(() => {
    const intervalId = window.setInterval(() => {
      const player = playerRef.current?.internalPlayer;

      if (!player?.getCurrentTime) {
        return;
      }

      Promise.resolve(player.getCurrentTime())
        .then((time: number) => {
          setCurrentTime(time);
        })
        .catch(() => {
          // Keep the UI usable if the embedded player is temporarily unavailable.
        });
    }, 250);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  React.useEffect(() => {
    if (play) {
      if (currentElapsedTime * 1000 >= currentPlayTime) {
        playerRef.current?.internalPlayer.pauseVideo();
        playerRef.current?.internalPlayer.seekTo(safeStartSeconds, true);
        setCurrentTime(safeStartSeconds);
        setPlay(false);
      }
    }
  }, [play, currentElapsedTime, currentPlayTime, safeStartSeconds]);

  React.useEffect(() => {
    if (previousIdRef.current === id) {
      return;
    }

    previousIdRef.current = id;
    setIsReady(false);
    setIsSlowToLoad(false);
    setPlayerMessage("");
    setCurrentTime(safeStartSeconds);
    setPlay(false);
  }, [id, safeStartSeconds]);

  React.useEffect(() => {
    if (isReady) {
      setIsSlowToLoad(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setIsSlowToLoad(true);
    }, 8000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isReady, id]);

  // don't call play video each time currentTime changes
  const startPlayback = React.useCallback(() => {
    const player = playerRef.current?.internalPlayer;

    if (!player?.playVideo) {
      setPlayerMessage("播放器还没准备好，请稍后再试。");
      return;
    }

    try {
      player.seekTo?.(safeStartSeconds, true);
      player.unMute?.();
      player.setVolume?.(100);

      const playResult = player.playVideo();
      setPlayerMessage("");
      setPlay(true);

      if (playResult && typeof playResult.catch === "function") {
        playResult.catch(() => {
          setPlay(false);
          setPlayerMessage(PLAYBACK_ERROR_MESSAGE);
        });
      }

      event({
        category: "Player",
        action: "Played song",
      });
    } catch {
      setPlay(false);
      setPlayerMessage(PLAYBACK_ERROR_MESSAGE);
    }
  }, [safeStartSeconds]);

  const setReady = React.useCallback(() => {
    setIsReady(true);
    setIsSlowToLoad(false);
    setPlayerMessage("");
    setCurrentTime(safeStartSeconds);
  }, [safeStartSeconds]);

  const handlePlayerError = React.useCallback(() => {
    setPlay(false);
    setPlayerMessage(LOAD_ERROR_MESSAGE);
  }, []);

  return (
    <>
      <Styled.HiddenYouTube>
        <YouTube
          opts={opts}
          videoId={id}
          onError={handlePlayerError}
          onReady={setReady}
          ref={playerRef}
        />
      </Styled.HiddenYouTube>
      {isReady ? (
        <>
          <Styled.ProgressBackground>
            {currentElapsedTime !== 0 && (
              <Styled.Progress value={currentElapsedTime} />
            )}
            {playTimes.map((playTime) => (
              <Styled.Separator
                style={{ left: `${(playTime / 16000) * 100}%` }}
                key={playTime}
              />
            ))}
          </Styled.ProgressBackground>
          <Styled.TimeStamps>
            <Styled.TimeStamp>1s</Styled.TimeStamp>
            <Styled.TimeStamp>16s</Styled.TimeStamp>
          </Styled.TimeStamps>
          <Styled.PlayButton
            aria-label="播放歌曲片段"
            onClick={startPlayback}
            type="button"
          >
            <IoPlay aria-hidden="true" size={40} color="#fff" />
          </Styled.PlayButton>
        </>
      ) : (
        <Styled.PlayerMessage role="status">
          {isSlowToLoad
            ? "播放器加载较慢，请确认当前网络可以访问 YouTube。"
            : "播放器加载中..."}
        </Styled.PlayerMessage>
      )}
      {playerMessage && (
        <Styled.PlayerMessage role="status">{playerMessage}</Styled.PlayerMessage>
      )}
    </>
  );
}
